import { Box, Text } from "@saleor/macaw-ui-next";
import { Check } from "lucide-react";
import React, { useMemo } from "react";
import useRouter from "use-react-router";

import { useWizardContext } from "../../context/WizardContext";
import { WizardStepId } from "../../types";
import { WIZARD_STEPS, wizardStepPath } from "../../urls";

export const WizardProgress: React.FC = () => {
  const { history } = useRouter();
  const { currentStepId, completedSteps, skippedSteps } = useWizardContext();

  const currentIndex = useMemo(
    () => WIZARD_STEPS.findIndex(s => s.id === currentStepId),
    [currentStepId],
  );

  const isStepDone = (id: WizardStepId) => completedSteps.includes(id) || skippedSteps.includes(id);

  const getStepState = (stepId: WizardStepId) => {
    if (isStepDone(stepId)) {
      return "completed";
    }

    if (stepId === currentStepId) return "current";

    return "upcoming";
  };

  const isStepReachable = (index: number) => {
    if (index === 0) return true;

    return isStepDone(WIZARD_STEPS[index - 1].id);
  };

  const handleStepClick = (stepId: WizardStepId, index: number) => {
    if (isStepReachable(index)) {
      const config = WIZARD_STEPS.find(s => s.id === stepId);

      if (config) {
        history.push(wizardStepPath(config.urlSegment));
      }
    }
  };

  return (
    <Box
      display="flex"
      alignItems="flex-start"
      width="100%"
      overflow="auto"
      paddingBottom={1}
      style={{
        scrollbarWidth: "none",
        msOverflowStyle: "none",
      }}
    >
      {WIZARD_STEPS.map((step, index) => {
        const state = getStepState(step.id);
        const Icon = step.icon;
        const isLast = index === WIZARD_STEPS.length - 1;
        const reachable = isStepReachable(index);

        return (
          <React.Fragment key={step.id}>
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              gap={1}
              cursor={reachable ? "pointer" : "auto"}
              onClick={() => handleStepClick(step.id, index)}
              __opacity={reachable ? 1 : 0.5}
              __flexShrink={0}
              style={{ transition: "opacity 0.2s ease" }}
            >
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                borderRadius={4}
                __width="42px"
                __height="42px"
                __backgroundColor={
                  state === "completed"
                    ? "#3b82f6"
                    : state === "current"
                      ? "#3b82f6"
                      : "rgba(148, 163, 184, 0.08)"
                }
                __color={state === "current" || state === "completed" ? "#ffffff" : "#667085"}
                style={{
                  border:
                    state === "completed"
                      ? "1px solid rgba(59, 130, 246, 0.9)"
                      : state === "current"
                        ? "1px solid rgba(59, 130, 246, 0.9)"
                        : "1px solid rgba(148, 163, 184, 0.16)",
                  boxShadow:
                    state === "current"
                      ? "0 12px 30px rgba(59, 130, 246, 0.18)"
                      : "none",
                  transition: "all 0.2s ease",
                }}
              >
                {state === "completed" ? (
                  <Check size={18} color="#ffffff" />
                ) : (
                  <Icon size={18} />
                )}
              </Box>
              <Text
                size={1}
                fontWeight={state === "current" ? "bold" : "regular"}
                color={state === "upcoming" ? "default2" : "default1"}
                __whiteSpace="nowrap"
                __textAlign="center"
              >
                {step.label}
              </Text>
            </Box>

            {!isLast && (
              <Box
                __flex={1}
                __height="2px"
                __marginTop="20px"
                __minWidth={{ mobile: "40px", tablet: "72px", desktop: "92px" }}
                __backgroundColor={
                  index < currentIndex ? "#3b82f6" : "rgba(148, 163, 184, 0.14)"
                }
                __borderRadius="1px"
                style={{
                  marginLeft: "8px",
                  marginRight: "8px",
                  transition: "background-color 0.3s ease",
                }}
              />
            )}
          </React.Fragment>
        );
      })}
    </Box>
  );
};
